/**
 * IndexedDB para capítulos baixados (leitura offline).
 * Evita expor URLs do CDN: as imagens são armazenadas como Blob após download via API.
 */

const DB_NAME = 'mediocre-offline';
const DB_VERSION = 2;
const STORE_CHAPTERS = 'offline_chapters';
const STORE_PAGES = 'offline_pages';
const STORE_COVERS = 'offline_covers';

export interface OfflineChapterMeta {
  chapterId: number;
  obraId: number;
  obraName: string;
  obraImage: string | null;
  chapterNum: number | string;
  chapterName: string;
  pageCount: number;
  savedAt: number;
  chapterType?: 'imagem' | 'texto';
  textMode?: 'texto' | 'markdown' | 'html';
  textContent?: string;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_CHAPTERS)) {
        db.createObjectStore(STORE_CHAPTERS, { keyPath: 'chapterId' });
      }
      if (!db.objectStoreNames.contains(STORE_PAGES)) {
        db.createObjectStore(STORE_PAGES, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_COVERS)) {
        db.createObjectStore(STORE_COVERS, { keyPath: 'obraId' });
      }
    };
  });
}

function getStore(db: IDBDatabase, storeName: string, mode: IDBTransactionMode = 'readonly') {
  return db.transaction(storeName, mode).objectStore(storeName);
}

export async function saveOfflineChapter(
  meta: OfflineChapterMeta,
  pages: Blob[]
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_CHAPTERS, STORE_PAGES], 'readwrite');
    const storeChapters = tx.objectStore(STORE_CHAPTERS);
    const storePages = tx.objectStore(STORE_PAGES);

    storeChapters.put(meta);
    pages.forEach((blob, index) => {
      storePages.put({ key: `${meta.chapterId}_${index}`, blob });
    });

    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

export async function listOfflineChapters(): Promise<OfflineChapterMeta[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const store = getStore(db, STORE_CHAPTERS);
    const req = store.getAll();
    req.onsuccess = () => {
      db.close();
      resolve((req.result || []).sort((a: OfflineChapterMeta, b: OfflineChapterMeta) => b.savedAt - a.savedAt));
    };
    req.onerror = () => {
      db.close();
      reject(req.error);
    };
  });
}

/** Agrupa capítulos por obra para a UI de "obras salvas". */
export function groupChaptersByObra(chapters: OfflineChapterMeta[]): Map<number, OfflineChapterMeta[]> {
  const map = new Map<number, OfflineChapterMeta[]>();
  for (const ch of chapters) {
    const list = map.get(ch.obraId) || [];
    list.push(ch);
    map.set(ch.obraId, list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => Number(a.chapterNum) - Number(b.chapterNum));
  }
  return map;
}

export async function getOfflinePageBlob(chapterId: number, pageIndex: number): Promise<Blob | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const store = getStore(db, STORE_PAGES);
    const req = store.get(`${chapterId}_${pageIndex}`);
    req.onsuccess = () => {
      db.close();
      const row = req.result;
      resolve(row?.blob ?? null);
    };
    req.onerror = () => {
      db.close();
      reject(req.error);
    };
  });
}

/** Salva a capa da obra (blob) para exibir offline na lista de downloads. */
export async function saveOfflineCover(obraId: number, blob: Blob): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const store = getStore(db, STORE_COVERS, 'readwrite');
    const req = store.put({ obraId, blob });
    req.onsuccess = () => {
      db.close();
      resolve();
    };
    req.onerror = () => {
      db.close();
      reject(req.error);
    };
  });
}

/** Retorna o blob da capa da obra se estiver salvo (para uso offline). */
export async function getOfflineCoverBlob(obraId: number): Promise<Blob | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const store = getStore(db, STORE_COVERS);
    const req = store.get(obraId);
    req.onsuccess = () => {
      db.close();
      const row = req.result;
      resolve(row?.blob ?? null);
    };
    req.onerror = () => {
      db.close();
      reject(req.error);
    };
  });
}

/** Remove a capa da obra do storage (ex.: ao remover o último capítulo da obra). */
export async function removeOfflineCover(obraId: number): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const store = getStore(db, STORE_COVERS, 'readwrite');
    const req = store.delete(obraId);
    req.onsuccess = () => {
      db.close();
      resolve();
    };
    req.onerror = () => {
      db.close();
      reject(req.error);
    };
  });
}

export async function hasOfflineChapter(chapterId: number): Promise<boolean> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const store = getStore(db, STORE_CHAPTERS);
    const req = store.get(chapterId);
    req.onsuccess = () => {
      db.close();
      resolve(!!req.result);
    };
    req.onerror = () => {
      db.close();
      reject(req.error);
    };
  });
}

export async function removeOfflineChapter(chapterId: number): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_CHAPTERS, STORE_PAGES], 'readwrite');
    const storeChapters = tx.objectStore(STORE_CHAPTERS);
    const storePages = tx.objectStore(STORE_PAGES);

    const reqMeta = storeChapters.get(chapterId);

    reqMeta.onsuccess = () => {
      const meta = reqMeta.result as OfflineChapterMeta | undefined;
      if (!meta) {
        db.close();
        resolve();
        return;
      }

      storeChapters.delete(chapterId);
      const pageCount = Number(meta.pageCount) || 0;
      for (let i = 0; i < pageCount; i++) {
        storePages.delete(`${chapterId}_${i}`);
      }
    };

    reqMeta.onerror = () => {
      db.close();
      reject(reqMeta.error);
    };

    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}
